// @flow

import * as React from 'react';
import {useContext, useState} from 'react';
import {useLocation, useNavigation, useArticle} from 'docs/data';
import {getNavItems} from './activateNavigation';

export type SidebarContextProps = {|
  open: boolean,
  setOpen: (((boolean) => boolean) | boolean) => void,
  show: boolean,
|};

const SidebarContext = React.createContext<SidebarContextProps>({
  open: false,
  show: true,
  setOpen: () => {},
});

type SidebarProviderProps = {|
  children: React.Node,
|};

export const useSidebar = () => {
  return useContext(SidebarContext);
};

const HIDDEN_SIDEBAR_PAGES = ['/docs', '/docs/products'];

export function SidebarProvider(props: SidebarProviderProps) {
  const {navigation} = useNavigation();
  const {location} = useLocation();
  const {article} = useArticle();
  const show = React.useMemo(
    () => !HIDDEN_SIDEBAR_PAGES.includes(location.pathname),
    [location.pathname],
  );

  const hasNavItems = !!getNavItems(navigation).length;
  // Set open to true when sidebar is not shown so the open animation doesn't occur on next page transition
  const [open, setOpen] = useState(hasNavItems);
  const forcedToClose = React.useRef(!hasNavItems);

  const context = React.useMemo(() => ({open, setOpen, show}), [open, show]);

  React.useEffect(() => {
    if (
      // Default to the sidebar closed if the user navigates to an IntegrationBuilder
      // on a narrow screen (<1500px)
      (article.metadata?.type === 'integration-builder' &&
        window.innerWidth <= 1500) ||
      // Close sidebar if there are no nav items
      (!hasNavItems && show)
    ) {
      setOpen((o) => {
        if (o) {
          forcedToClose.current = true;
        }
        return false;
      });
    } else if (hasNavItems && forcedToClose.current) {
      // Reopen the sidebar if the user leaves the IntegrationBuilder
      setOpen(true);
      forcedToClose.current = false;
    }
  }, [location, hasNavItems, show, article]);

  return (
    <SidebarContext.Provider value={context}>
      {props.children}
    </SidebarContext.Provider>
  );
}
